import { isNeo4jError } from '@nx/errors';
import moment from 'moment';
import { type Neo4jError, error as driverError } from 'neo4j-driver';

import { isProcedureRegistrationFailedError } from '../services/errors/errors';
import { errorMapper } from '../services/queries/errorMapper';

export const MSG_USER_NOT_AUTHORIZED =
  "The Neo4j instance you're connecting to has been configured to limit Bloom access to users with authorized roles as defined by the database administrator. A database administrator can configure your Neo4j account with the needed role(s), or allow access to additional Neo4j users.";
export const MSG_SCENE_NOT_FOUND =
  'Could not find Scene. The Scene has either been deleted or the link has been modified. Contact your database administrator if the issue persists.';
export const MSG_SCENE_NO_ACCESS = 'Could not access Scene. Contact your database administrator if the issue persists.';
export const MSG_SCENE_NO_ACCESS_TITLE = 'Could not access Scene';
export const MSG_SCENE_NO_ACCESS_SUBTITLE = (user: string): string => `Current user is ${user}`;
export const MSG_SCENE_NO_ACCESS_DESCRIPTION = 'Contact your database administrator if the issue persists.';

export const MSG_REQUIRES_AUTH_ENABLED =
  'Neo4j Bloom requires auth to be enabled. Please contact your system administrator.';
export const MSG_REQUIRES_ENTERPRISE_EDITION = 'Neo4j Bloom requires Neo4j Enterprise Edition.';
export const MSG_MISSING_LINK_TARGET = 'Could not find link target. Please check your link configuration.';
export const MSG_BLOOM_SERVER_LICENSE_EXPIRED =
  'The Neo4j Bloom Server license is expired. Please contact your system administrator.';
export const MSG_BLOOM_SERVER_PLUGIN_MISSING =
  'The Neo4j Bloom Server plugin is missing. A licensed plugin is required for remote graphs. Please contact your system administrator or your Neo4j representative.';
export const MSG_BLOOM_SERVER_LICENSE_MISSING =
  "We couldn't find a license for the Neo4j Bloom Server. Please check with your system administrator.";
export const MSG_BLOOM_SERVER_LICENSE_INVALID =
  "The Neo4j Bloom Server license couldn't be verified. Please contact your system administrator.";
export const MSG_BLOOM_SERVER_LICENSE_EXPIRING = (
  daysLeft: number,
  currentDate: string | number | Date = Date(),
): string =>
  `The Neo4j Bloom Server license expires on ${moment(currentDate).add(daysLeft, 'days').format('MMMM Do, YYYY')} (${
    daysLeft === 0 ? 'today' : `in ${daysLeft} days`
  }).`;
export const MSG_NOT_SUPPORTED_VERSION = (version: string): string =>
  `Bloom supports Neo4j database versions ${version} and above. Please upgrade your database to a supported version.`;

const MSG_CANNOT_CONNECT_DB = 'Cannot connect to database. Please ensure that the database is up and available.';

const MSG_PROCEDURE_UNRESTRICTED =
  'Configuration issue detected: Bloom Enterprise requires permission to run procedures in unrestricted mode, which can be configured in the neo4j.conf file';

const noErrorMessage = '{}';

const _getErrorText = (error: Neo4jError): string => {
  if (isNeo4jError(error)) {
    const { gqlStatusDescription, gqlStatus } = error;
    const errorText = error.message ?? gqlStatusDescription ?? JSON.stringify(error);
    return errorText === noErrorMessage ? (error.code ?? gqlStatus) : errorText;
  }
  return noErrorMessage;
};

export const getErrorMessage = (e: unknown) => {
  const error = errorMapper(e);
  if (!isNeo4jError(error)) {
    return undefined;
  }

  let message;
  let title;

  const cannotConnectToDb = [driverError.SERVICE_UNAVAILABLE, driverError.SESSION_EXPIRED].includes(error.code);

  if (cannotConnectToDb) {
    title = 'Cannot connect to the database';
    message = MSG_CANNOT_CONNECT_DB;
  } else if (isProcedureRegistrationFailedError(error)) {
    message = _getErrorText(error);
    if (message.startsWith('bloom.checkAuthorization')) {
      message = MSG_PROCEDURE_UNRESTRICTED;
    }
  } else {
    message = _getErrorText(error);
  }

  return {
    code: isNeo4jError(error) ? (error.gqlStatus ?? error.code) : 'UnknownError',
    title,
    message,
  };
};
